import React from "react";
import {Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import {AlertTriangle, Check, MapPin} from "lucide-react";

const TrackingTimelineRow = ({event, isLast, isFirst}) => {
    const theme = useTheme();
    const location = [event.city, event.province].filter(Boolean).join(", ");

    return (
        <Box sx={{
            display: "grid",
            gridTemplateColumns: {xs: "20px 1fr", md: "20px minmax(0, 1fr) 160px"},
            gap: {
                xs: 0.75,
                md: 1.25
            }, ...(isFirst && !event.warning ? {
                backgroundColor: alpha(theme.palette.success.main, 0.08),
                borderRadius: `${theme.shape.lg}px`,
                mx: -1,
                px: 1,
                pt: 0.5
            } : null)
        }}>
            <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", height: "100%"}}>
                <Box sx={{
                    width: event.done ? 9 : 8,
                    height: event.done ? 9 : 8,
                    mt: 0.15,
                    borderRadius: "50%",
                    backgroundColor: event.done ? "primary.main" : "background.paper",
                    border: `2px solid ${event.done ? theme.palette.primary.main : theme.palette.primary[100]}`,
                    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, event.done ? 0.06 : 0.02)}`
                }}/>
                {!isLast ? <Box sx={{
                    width: 2,
                    flex: 1,
                    my: 0.25,
                    borderRadius: theme.shape.xl,
                    backgroundColor: "neutral.100"
                }}/> : null}
            </Box>
            <Box sx={{pb: isLast ? 0 : 1.25}}>
                <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                    <Typography variant="subtitle2" sx={{
                        color: event.done ? "text.primary" : "text.secondary",
                        mb: 0.125,
                        lineHeight: 1.2
                    }}>{event.status}</Typography>
                    {event.warning ? <AlertTriangle size={15} strokeWidth={2.25} color={theme.palette.warning.main}
                                                    aria-label="Warning"/> : null}
                </Box>
                {event.description ? <Typography variant="caption" sx={{
                    display: "block",
                    color: "neutral.500",
                    lineHeight: 1.25,
                    mb: location ? 0.375 : 0
                }}>{event.description}</Typography> : null}
                {location ? (
                    <Box sx={{display: "flex", alignItems: "center", gap: 0.375}}>
                        <MapPin size={13} strokeWidth={2.25} color={theme.palette.success.main} aria-hidden="true"/>
                        <Typography variant="caption"
                                    sx={{color: "success.main", lineHeight: 1.25}}>{location}</Typography>
                    </Box>
                ) : null}
            </Box>
            <Typography variant="mono" color="text.secondary" sx={{
                display: {xs: "none", md: "block"},
                textAlign: "right",
                whiteSpace: "pre-line",
                fontSize: 11,
                lineHeight: 1.25,
                pt: 0.05
            }}>{event.time}</Typography>
        </Box>
    );
};

const COLUMNS = ["Description", "TimeStamp", "Location"];

const MobileHistoryCard = ({event, isFirst}) => {
    const theme = useTheme();
    const location = [event.city, event.province].filter(Boolean).join(", ");
    return (
        <Box sx={{
            p: 1.25,
            borderBottom: 1,
            borderColor: "divider",
            backgroundColor: isFirst && event.done ? alpha(event.warning ? theme.palette.warning.main : theme.palette.success.main, 0.08) : "transparent",
            "&:last-child": {borderBottom: 0}
        }}>
            <Box sx={{display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1}}>
                <Box sx={{display: "flex", alignItems: "center", gap: 0.75, minWidth: 0}}>
                    {event.warning ? <AlertTriangle size={16} color={theme.palette.warning.main}
                                                    aria-label="Warning"/> : event.done ? (
                        <Box sx={{
                            width: 18,
                            height: 18,
                            flexShrink: 0,
                            borderRadius: "50%",
                            backgroundColor: alpha(theme.palette.success.main, 0.12),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}><Check size={12} strokeWidth={3} color={theme.palette.success.main}/></Box>
                    ) : <Box
                        sx={{width: 7, height: 7, mx: 0.75, borderRadius: "50%", backgroundColor: "neutral.300"}}/>}
                    <Typography variant="subtitle2" sx={{lineHeight: 1.35}}>{event.status}</Typography>
                </Box>
                <Typography variant="caption"
                            sx={{flexShrink: 0, color: "text.secondary", textAlign: "right"}}>{event.time}</Typography>
            </Box>
            {event.description && event.description !== event.status ? <Typography variant="body2" sx={{
                mt: 0.5,
                color: "text.secondary",
                fontSize: 14,
                lineHeight: 1.45
            }}>{event.description}</Typography> : null}
            {location ? <Box sx={{display: "flex", alignItems: "center", gap: 0.5, mt: 0.75}}><MapPin size={14}
                                                                                                      color={theme.palette.success.main}
                                                                                                      aria-hidden="true"/><Typography
                variant="caption" sx={{color: "success.main"}}>{location}</Typography></Box> : null}
        </Box>
    );
};

const TrackingHistoryTable = ({events}) => {
    const theme = useTheme();
    return (
        <>
            <Box sx={{display: {xs: "block", sm: "none"}}}>
                {events.map((event, index) => <MobileHistoryCard key={`${event.status}-${index}`} event={event}
                                                                 isFirst={index === 0}/>)}
            </Box>
            <TableContainer sx={{display: {xs: "none", sm: "block"}, overflowX: "hidden"}}>
                <Table size="small" aria-label="Tracking history" sx={{width: "100%", tableLayout: "fixed"}}>
                    <TableHead>
                        <TableRow sx={{backgroundColor: "background.default"}}>
                            {COLUMNS.map((column, index) => <TableCell key={column}
                                                                       sx={{width: ["48%", "26%", "26%"][index]}}><Typography
                                variant="tableHeader" sx={{
                                whiteSpace: "nowrap",
                                display: "block", ...(column === "Description" ? {pl: 3} : null)
                            }}>{column}</Typography></TableCell>)}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {events.map((event, index) => (
                            <TableRow key={`${event.status}-${index}`}
                                      sx={index === 0 && event.done ? {backgroundColor: alpha(event.warning ? theme.palette.warning.main : theme.palette.success.main, 0.08)} : undefined}>
                                <TableCell sx={{
                                    width: "48%",
                                    color: event.done ? "text.primary" : "text.secondary",
                                    overflowWrap: "anywhere"
                                }}>
                                    <Box sx={{display: "flex", alignItems: "flex-start", gap: 1, py: 0.5}}>
                                        <Box sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            height: 20,
                                            width: 16,
                                            justifyContent: "center",
                                            flexShrink: 0
                                        }}>
                                            {event.warning ? <AlertTriangle size={15} strokeWidth={2.25}
                                                                            color={theme.palette.warning.main}
                                                                            aria-label="Warning"/> : event.done ? (
                                                <Box sx={{
                                                    width: 16,
                                                    height: 16,
                                                    borderRadius: "50%",
                                                    backgroundColor: alpha(theme.palette.success.main, 0.12),
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center"
                                                }}><Check size={11} strokeWidth={3.5} color={theme.palette.success.main}
                                                          aria-hidden="true"/></Box>
                                            ) : <Box sx={{
                                                width: 6,
                                                height: 6,
                                                borderRadius: "50%",
                                                backgroundColor: "neutral.300"
                                            }}/>}
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{
                                                color: event.done ? "text.primary" : "text.secondary",
                                                lineHeight: 1.4
                                            }}>{event.status}</Typography>
                                            {event.description && event.description !== event.status ?
                                                <Typography variant="caption" sx={{
                                                    color: "text.secondary",
                                                    opacity: 0.8,
                                                    display: "block",
                                                    mt: 0.25,
                                                    lineHeight: 1.3
                                                }}>{event.description}</Typography> : null}
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{
                                    width: "26%",
                                    color: "text.secondary",
                                    verticalAlign: "top",
                                    pt: 1.75,
                                    overflowWrap: "anywhere"
                                }}>{event.time}</TableCell>
                                <TableCell sx={{
                                    width: "26%",
                                    color: "text.secondary",
                                    verticalAlign: "top",
                                    pt: 1.75,
                                    overflowWrap: "anywhere"
                                }}>{[event.address, event.city, event.province, event.country].filter(Boolean).join(", ")}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

export const TrackingHistory = ({events, layout = "timeline"}) => layout === "table" ? (
    <TrackingHistoryTable events={events}/>
) : (
    <Box sx={{mt: 0.25}}>
        {events.map((event, index) => <TrackingTimelineRow key={`${event.status}-${index}`} event={event}
                                                           isLast={index === events.length - 1}
                                                           isFirst={index === 0}/>)}
    </Box>
);
